import React, { useState } from 'react';
import {
  Button,
  Group,
  Box,
  Text,
  Flex,
  Modal,
  useMantineTheme,
  Title,
  Center,
  Stack,
  Code,
  Input,
  MultiSelect,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconCheck } from '@tabler/icons';
import { NavLink, useNavigate } from 'react-router-dom';
import { Technology } from '../../../backend/models/technology';
import { addPerson, deleteClient } from '../../Util/server';
import FloatingLabelInput from '../../Components/Input';
import { Client, SubmittableClient } from '../../../backend/models/client';
import { Person } from '../../../backend/models/person';
import RichText from '../../Components/RichText';

type ClientFormType = {
  data?: Client;
  persons: Person[];
  technologies: Technology[];
  names: string[];
  onSubmit: (values: SubmittableClient) => Promise<Client>;
  buttonLabel: string;
  add: boolean;
  successMessage: string;
};

export const DEFAULT_BLIP_COLOR = '#212529';

const ClientForm = (props: ClientFormType) => {
  const navigate = useNavigate();
  const theme = useMantineTheme();
  const [loading, setLoading] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [deleted, setDeleted] = useState<boolean>(false);
  const [persons, setPersons] = useState<Person[]>(props.persons);
  const [removeModalOpen, setRemoveModalOpen] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [confirmationText, setConfirmationText] = useState<string>('');

  const form = useForm<SubmittableClient>({
    initialValues: {
      name: props.data?.name ?? '',
      url: props.data?.url ?? '',
      description: props.data?.description ?? '',
      salesforceNumber: props.data?.salesforceNumber,
      solutionArchitectIds:
        props.data?.personRelations?.filter((r) => r.relationType === 'solutionArchitect').map((r) => r.personId) ?? [],
      technologyIds: props.data?.technologies?.map((c) => c.id) ?? [],
    },

    transformValues: (values) => ({
      name: values.name.trim(),
      url: values.url?.trim(),
      description: values.description,
      salesforceNumber: values.salesforceNumber,
      solutionArchitectIds: values.solutionArchitectIds,
      technologyIds: values.technologyIds,
    }),

    validate: {
      name: (value) =>
        !!value.trim() ? (
          props.names
            .filter((n) => n !== props.data?.name)
            .map((n) => n.toLocaleLowerCase())
            .includes(value.toLocaleLowerCase().trim()) ? (
            <>
              Client already <NavLink to={`/client/${encodeURIComponent(value.toLocaleLowerCase())}`}>exists</NavLink>
            </>
          ) : null
        ) : (
          'Name is required'
        ),
    },
  });

  const handleSubmit = async (values: SubmittableClient) => {
    setLoading(true);
    props.onSubmit(values).then((res) => {
      navigate(`/client/${encodeURIComponent(res.name)}`);
    });
  };

  const handleDelete = () => {
    setDeleting(true);
    setDeleting(false);
    // id is always present if we're in Update mode
    if (props.data?.id) {
      deleteClient(props.data.id).then(() => {
        setDeleted(true);
        setRemoveModalOpen(false);
        setMessage('Client deleted successfully');
      });
    }
  };

  const createPerson = (name: string, callback: (id: string) => void) => {
    setLoading(true);
    async function doCreate() {
      addPerson(name)
        .then((res) => {
          callback(res.id);
          setPersons((oldPersons) => [...oldPersons, res]);
          setLoading(false);
          setMessage('Person created successfully');
        })
        .catch(() => {
          setLoading(false);
        });
    }

    doCreate();
  };

  return (
    <Box sx={{ maxWidth: '100%', width: 600 }} mx="auto">
      <form onSubmit={form.onSubmit((values) => handleSubmit(values))}>
        <FloatingLabelInput
          label="Client name"
          placeholder="Add name"
          required
          withAsterisk
          disabled={deleted}
          {...form.getInputProps('name')}
        />
        <FloatingLabelInput
          label="Salesforce number"
          placeholder="Salesforce number"
          mt={'xl'}
          disabled={deleted}
          {...form.getInputProps('salesforceNumber')}
        />
        <MultiSelect
          data={persons.map((v) => ({
            value: v.id,
            label: v.name,
          }))}
          label="Solution architects"
          placeholder="Solution architects for this client"
          mt={'xl'}
          disabled={deleted}
          creatable
          searchable
          getCreateLabel={(query) => `+ Add '${query}'`}
          onCreate={(query) => {
            createPerson(query.trim(), (id: string) => {
              form.setFieldValue('solutionArchitectIds', [...(form.values.solutionArchitectIds ?? []), id]);
            });
            return null;
          }}
          {...form.getInputProps('solutionArchitectIds')}
        />
        <MultiSelect
          data={props.technologies
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((t) => {
              return { value: t.id, label: t.name, group: t.section?.name };
            })}
          label="Technologies"
          placeholder="Technologies used by this client"
          mt={'xl'}
          disabled={deleted}
          searchable
          {...form.getInputProps('technologyIds')}
          maxDropdownHeight={400}
        />
        <FloatingLabelInput
          label="Homepage URL"
          placeholder="https://..."
          mt={'xl'}
          disabled={deleted}
          {...form.getInputProps('url')}
        />
        <RichText label="Description" disabled={deleted} {...form.getInputProps('description')} />

        <Flex justify={'space-between'} align={'center'} mt="md">
          {
            <Group align={'center'}>
              {message && (
                <>
                  <Text color={'green'}>{message}</Text>
                  <IconCheck color={'green'} />
                </>
              )}
            </Group>
          }
          <Group position="right">
            {!props.add && (
              <Button color="red" onClick={() => setRemoveModalOpen(true)} disabled={deleted}>
                Remove
              </Button>
            )}
            <Button type="submit" loading={loading} disabled={deleted}>
              {props.buttonLabel}
            </Button>
          </Group>
        </Flex>
      </form>

      <Modal
        opened={removeModalOpen}
        onClose={() => {
          setRemoveModalOpen(false);
          setConfirmationText('');
        }}
        centered
        size={'lg'}
        title={<Title order={4}>{'Remove client from radar?'}</Title>}
        overlayColor={theme.colors.gray[2]}
        overlayOpacity={0.55}
        overlayBlur={3}
      >
        <Center>
          <Stack mt="lg" spacing={'sm'}>
            <Text>{`Are you sure you want to remove ${props.data?.name} as a client?`}</Text>
            <div>
              <Text display={'inline'}>{'If so, type'}</Text>
              <Code>{props.data?.name}</Code>
              <Text display={'inline'}>{'in the input below and then click the button'}</Text>
            </div>
            <Group mt={'xs'}>
              <Input
                value={confirmationText}
                onChange={(event) => setConfirmationText(event.currentTarget.value)}
                style={{ display: 'flex', flexGrow: 1 }}
              />
              <Button
                color="red"
                disabled={confirmationText !== props.data?.name}
                onClick={() => handleDelete()}
                loading={deleting}
                loaderPosition={'center'}
              >
                YES, I'M SURE
              </Button>
            </Group>
          </Stack>
        </Center>
      </Modal>
    </Box>
  );
};

export default ClientForm;
